<script lang="ts">
  import { Signos, Notas, type Signo } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';
  import FisioterapiaEvaluacionesPanel from './FisioterapiaEvaluacionesPanel.svelte';
  import { isClinicaFisioterapia } from '../lib/fisioterapia';
  import { evaluacionesDesdeNotas } from '../lib/fisioterapia-evaluaciones';
  import { session } from '../lib/auth.svelte';
  import { autorFisioterapista } from '../lib/fisioterapia';

  let {
    orgTipo = null,
  }: {
    orgTipo?: string | null;
  } = $props();

  const esFisio = $derived(isClinicaFisioterapia(orgTipo));
  const autor = $derived(autorFisioterapista({ usuario: session.value?.usuario }));

  let pacienteId = $state('');
  let signos = $state<Signo[]>([]);
  let notas = $state<import('../lib/api').Nota[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);

  let nuevo = $state<Signo>({
    pacienteId: '', ta: '', fc: null, fr: null, temp: null, satO2: null, peso: null, talla: null
  });

  async function cargar() {
    if (!pacienteId) { signos = []; notas = []; return; }
    loading = true; error = null;
    try {
      const [s, n] = await Promise.all([
        Signos.porPaciente(pacienteId),
        Notas.porPaciente(pacienteId).catch(() => []),
      ]);
      signos = s;
      notas = n;
    } catch (e: unknown) { error = e instanceof Error ? e.message : String(e); }
    finally { loading = false; }
  }

  async function registrar(e: SubmitEvent) {
    e.preventDefault();
    if (!pacienteId) { error = 'Selecciona un paciente.'; return; }
    error = null;
    try {
      const body: Signo = {
        pacienteId,
        ta: nuevo.ta || null,
        fc: nuevo.fc != null ? Number(nuevo.fc) : null,
        fr: nuevo.fr != null ? Number(nuevo.fr) : null,
        temp: nuevo.temp != null ? Number(nuevo.temp) : null,
        satO2: nuevo.satO2 != null ? Number(nuevo.satO2) : null,
        peso: nuevo.peso != null ? Number(nuevo.peso) : null,
        talla: nuevo.talla != null ? Number(nuevo.talla) : null
      };
      await Signos.registrar(body);
      nuevo = { pacienteId: '', ta: '', fc: null, fr: null, temp: null, satO2: null, peso: null, talla: null };
      await cargar();
    } catch (e: unknown) { error = e instanceof Error ? e.message : String(e); }
  }

  $effect(() => { if (pacienteId) cargar(); });

  const evalCount = $derived(evaluacionesDesdeNotas(notas).length);
</script>

<section>
  <header><h2>{esFisio ? 'Evaluaciones fisioterapia' : 'Signos vitales'}</h2></header>
  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if esFisio}
    <p class="hint">Registre evaluaciones por objetivo: dolor, edema, sensibilidad, fuerza, reflejos, ROM e integral. Las plantillas son editables.</p>
  {/if}

  <div class="picker-row">
    <PacientePicker bind:value={pacienteId} onPick={(id) => pacienteId = id} placeholder="Paciente…" />
    <button onclick={cargar} disabled={loading || !pacienteId}>{loading ? '…' : 'Recargar'}</button>
  </div>

  {#if pacienteId}
    {#if esFisio}
      <FisioterapiaEvaluacionesPanel {pacienteId} {autor} {notas} onSaved={cargar} />
      <p class="meta-hist">{evalCount} evaluación(es) en expediente</p>
    {:else}
      <h3>Registrar toma</h3>
      <form onsubmit={registrar} class="form-grid">
        <label>TA <input placeholder="120/80" bind:value={nuevo.ta} /></label>
        <label>FC <input type="number" min="0" bind:value={nuevo.fc} /></label>
        <label>FR <input type="number" min="0" bind:value={nuevo.fr} /></label>
        <label>Temp °C <input type="number" step="0.1" bind:value={nuevo.temp} /></label>
        <label>SatO₂ % <input type="number" min="0" max="100" bind:value={nuevo.satO2} /></label>
        <label>Peso kg <input type="number" step="0.1" bind:value={nuevo.peso} /></label>
        <label>Talla cm <input type="number" step="0.1" bind:value={nuevo.talla} /></label>
        <button type="submit" class="full">Registrar</button>
      </form>

      <h3 style="margin-top:1.5rem">Historial</h3>
      <table>
        <thead><tr><th>Fecha</th><th>TA</th><th>FC</th><th>FR</th><th>T°</th><th>SatO₂</th><th>Peso</th><th>Talla</th></tr></thead>
        <tbody>
          {#each signos as s (s.id)}
            <tr>
              <td>{s.registradoEn ? new Date(s.registradoEn).toLocaleString() : '—'}</td>
              <td>{s.ta ?? '—'}</td><td>{s.fc ?? '—'}</td><td>{s.fr ?? '—'}</td>
              <td>{s.temp ?? '—'}</td><td>{s.satO2 ?? '—'}</td>
              <td>{s.peso ?? '—'}</td><td>{s.talla ?? '—'}</td>
            </tr>
          {:else}<tr><td colspan="8" class="empty">No hay registros de signos vitales.</td></tr>{/each}
        </tbody>
      </table>
    {/if}
  {:else}
    <p class="hint">Selecciona un paciente para ver/registrar {esFisio ? 'evaluaciones' : 'signos vitales'}.</p>
  {/if}
</section>

<style>
  header { display: flex; justify-content: space-between; align-items: center; }
  h3 { margin: 1rem 0 .5rem; font-size: 1rem; color: #555; }
  .picker-row { display: flex; gap: .5rem; align-items: center; }
  .picker-row :global(.picker) { flex: 1; }
  .form-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: .5rem; }
  .form-grid label { display: flex; flex-direction: column; font-size: .8rem; color: #555; }
  .form-grid input { padding: .45rem .6rem; border-radius: 8px; border: 1px solid #ccc; font: inherit; }
  .full { grid-column: span 4; background: #0d6efd; color: #fff; border: 0; padding: .5rem; border-radius: 8px; cursor: pointer; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: .4rem .55rem; border-bottom: 1px solid #eee; text-align: left; font-size: .85rem; font-variant-numeric: tabular-nums; }
  .empty { text-align: center; color: #888; font-style: italic; }
  .hint { color: #888; font-style: italic; margin-top: 1rem; }
  .meta-hist { font-size: 0.82rem; color: #666; margin-top: 0.5rem; }
  .err { color: #b00020; }
  button { padding: .45rem .8rem; border-radius: 8px; border: 1px solid #0d6efd; background: #fff; color: #0d6efd; cursor: pointer; }
</style>
